import type * as React from 'react';
import { cn } from '../../lib/cn.ts';

export interface WeekPreviewProps extends Omit<React.ComponentProps<'div'>, 'style'> {
  /** The small grey line above the range, e.g. "Deze week". */
  title: string;
  /** Which days it covers, e.g. "15 – 19 juni". */
  range: string;
  /** The green line under the list, saying what the marked rows mean. */
  footer?: React.ReactNode;
}

/**
 * A miniature of the user's own week, shown beside a choice that changes it.
 *
 * Not an agenda: rows carry a time and a name and nothing else, and there is no
 * way to open one. It exists to make a setting concrete — "these are the
 * conversations that answer would pick up" — so what it has to get right is the
 * marking, not the detail.
 */
export function WeekPreview({
  title,
  range,
  footer,
  className,
  children,
  ...props
}: WeekPreviewProps) {
  return (
    <div
      data-slot='week-preview'
      className={cn(
        'w-full overflow-hidden rounded-md border border-line bg-surface shadow-menu',
        className,
      )}
      {...props}
    >
      <div className='border-b border-fill px-4 pt-3.5 pb-2.5'>
        <p className='text-xs font-bold tracking-wide text-ink-muted uppercase'>
          {title}
        </p>
        <p className='mt-0.5 text-md font-bold text-ink'>{range}</p>
      </div>
      <div className='flex flex-col gap-2 px-3 py-2.5'>{children}</div>
      {footer && (
        <p className='flex items-center gap-2 border-t border-fill px-4 py-2.5 text-xs font-bold text-success'>
          <span aria-hidden='true' className='size-1.5 shrink-0 rounded-full bg-accent' />
          {footer}
        </p>
      )}
    </div>
  );
}

export type WeekPreviewDayProps = Omit<React.ComponentProps<'p'>, 'style'>;

/** The day a run of rows belongs to. Repeated per day, never per row. */
export function WeekPreviewDay({ className, ...props }: WeekPreviewDayProps) {
  return (
    <p
      data-slot='week-preview-day'
      className={cn(
        'px-0.5 pt-1 text-2xs font-bold tracking-wide text-ink-muted uppercase first:pt-0',
        className,
      )}
      {...props}
    />
  );
}

export interface WeekPreviewRowProps extends Omit<
  React.ComponentProps<'div'>,
  'style' | 'title'
> {
  /** Start time, e.g. "10:00". */
  time: string;
  title: string;
  /** What kind of conversation it is. Only ever shown on a marked row. */
  badge?: React.ReactNode;
  /*
   * Whether Wundo takes this one on. The whole point of the preview: an unmarked
   * row is still the user's meeting, it is just not one Wundo prepares.
   */
  marked?: boolean;
}

export function WeekPreviewRow({
  time,
  title,
  badge,
  marked = false,
  className,
  ...props
}: WeekPreviewRowProps) {
  return (
    <div
      data-slot='week-preview-row'
      data-marked={marked || undefined}
      className={cn(
        'flex items-center gap-2.5 rounded-sm border px-2.5 py-2 transition',
        marked ? 'border-accent-line bg-accent-tint' : 'border-transparent bg-fill-faint',
        className,
      )}
      {...props}
    >
      <span className='w-9.5 shrink-0 text-2xs font-bold text-ink-muted'>{time}</span>
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-sm font-bold',
          marked ? 'text-ink' : 'text-ink-muted',
        )}
      >
        {title}
      </span>
      {badge}
    </div>
  );
}
